import {
    ICatalogFilterCheckbox,
    ICatalogQueryParams,
    TCatalogFilterType,
} from 'tkmp-lib/src/interfaces';

/**
 * Сервис для управления параметрами запросов каталога и формирования URL.
 */
export const CatalogQueryService = {
    /**
     * Создает объект параметров запроса каталога из строки запроса или массива строк.
     * @param query_string - Строка запроса или массив строк для разбора.
     * @returns Объект типа ICatalogQueryParams с разобранными параметрами запроса.
     */
    generateCatalogQuery: function (query_string: string | string[]): ICatalogQueryParams {
        const newCatalogType: ICatalogQueryParams = {
            isBrand: false,
            isApply: false,
            isFilter: false,
            query_params: [],
            filter_params: [],
            brand_name: '',
        };
        if (!query_string) return newCatalogType;
        const paramsArray: string[] = Array.isArray(query_string) ? query_string : [query_string];

        newCatalogType.isBrand = paramsArray.indexOf('brand') !== -1;
        newCatalogType.isFilter = paramsArray.indexOf('filter') !== -1;
        newCatalogType.isApply = paramsArray.indexOf('apply') !== -1;

        const regexFilter = /((.*?)-from(.*)-to-(.*)|(.*?)-is-(.*)|(apply))/gm;

        newCatalogType.query_params =
            paramsArray?.indexOf('brand') !== -1 // есть бренд?
                ? paramsArray.slice(0, paramsArray.indexOf('brand'))
                : paramsArray?.indexOf('filter') !== -1 // есть фильтр?
                  ? paramsArray.slice(0, paramsArray.indexOf('filter'))
                  : paramsArray;

        newCatalogType.query_params = newCatalogType.query_params.filter(
            (f) => !regexFilter.test(f),
        ); // отбросить все фильтры и прочее

        if (newCatalogType.isBrand) {
            newCatalogType.brand_name =
                paramsArray.slice(paramsArray.indexOf('brand')).splice(1).length > 0
                    ? paramsArray
                          .slice(paramsArray.indexOf('brand'), paramsArray.indexOf('brand') + 2)
                          .splice(1)[0]
                    : '';
        } else {
            newCatalogType.brand_name = '';
        }
        if (newCatalogType.isFilter) {
            newCatalogType.filter_params = paramsArray
                .slice(paramsArray.indexOf('filter'), paramsArray.indexOf('apply'))
                .splice(1)
                .map((parameter) => {
                    const obj: TCatalogFilterType = {
                        property: '',
                        values: [],
                        isRange: false,
                    };

                    if (parameter.split('-from-').length > 1) {
                        // isRange
                        obj.property = parameter.split('-from-')[0];
                        obj.values = parameter.split('-from-')[1].split('-to-');
                        obj.isRange = true;
                    } else if (parameter.split('-is').length > 1) {
                        obj.property = parameter.split('-is-')[0];
                        obj.values = parameter.split('-is-')[1].split('-and-');
                    } else {
                        return obj;
                    }
                    return obj;
                });
        } else {
            newCatalogType.filter_params = [];
        }

        return newCatalogType;
    },

    /**
     * Формирует URL-путь на основе параметров запроса каталога.
     * @param catalogType - Параметры запроса каталога для преобразования в URL.
     * @param responseVariable - Данные фильтра (чекбокса) для включения в URL.
     * @returns Сформированная строка URL.
     */
    getNewUrl: function (
        catalogType: ICatalogQueryParams,
        responseVariable: ICatalogFilterCheckbox,
    ): string {
        if (
            catalogType.filter_params.filter((f) => f.property === responseVariable.property)
                .length > 0
        ) {
            const parameter: TCatalogFilterType | undefined = catalogType.filter_params.find(
                (f) => f.property === responseVariable.property,
            );

            // @ts-ignore
            if (parameter.values.find((f) => f === responseVariable.value)) {
                // @ts-ignore
                catalogType = this.exclude(catalogType, responseVariable, parameter);
            } else {
                // нет текущего значения в целом фильтре
                // @ts-ignore
                catalogType = this.include(catalogType, responseVariable, parameter);
            }
        } else {
            // не существует активного целого фильтра
            catalogType = this.add(catalogType, responseVariable);
        }

        return this.build(catalogType);
    },

    /**
     * Формирует URL-путь для fetch запроса на основе параметров запроса каталога.
     * @param catalogType - Параметры запроса каталога для преобразования в URL.
     * @returns Сформированная строка URL c query параметрами.
     */
    getFilterUrl: function (catalogType: ICatalogQueryParams) {
        if (!catalogType?.filter_params) return '';

        const queryParts = catalogType.filter_params
            .map((filterCategory) => {
                if (filterCategory.isRange) {
                    return [];
                } else {
                    return filterCategory.values.map(
                        (value) => `${filterCategory.property}=${value}`,
                    );
                }
            })
            .reduce((acc, val) => acc.concat(val), []); // сплющиваем массивы

        const query = queryParts.length ? '?' + queryParts.join('&') : '';
        console.log('CatalogQueryService: result query - ', query);
        return query;
    },

    /**
     * Возвращает существующие ключи категорий в параметрах запроса каталога.
     * @param catalogType - Параметры запроса каталога для проверки.
     * @returns Массив ключей категорий или объект, представляющий категории.
     */
    existsCategoryKeys: function (catalogType: ICatalogQueryParams): string[] {
        return catalogType.filter_params.map((param) => param.property);
    },

    /**
     * Проверяет, существует ли указанное значение в категории в параметрах запроса.
     * @param catalogType - Параметры запроса каталога для проверки.
     * @param categoryName - Название категории для поиска.
     * @param categoryValue - Значение, которое нужно проверить в категории.
     * @returns Булево значение, указывающее, существует ли значение в указанной категории.
     */
    existsValue: function (
        catalogType: ICatalogQueryParams,
        categoryName: string,
        categoryValue: string,
    ): boolean {
        if (catalogType.filter_params.filter((f) => f.property === categoryName).length > 0) {
            if (
                catalogType.filter_params
                    .find((f) => f.property === categoryName)!
                    .values.filter((f) => f === categoryValue).length > 0
            ) {
                return true;
            }
        }
        return false;
    },

    /**
     * Собирает часть URL, содержащую параметры фильтров.
     * @param catalogType - Параметры запроса каталога для формирования строки фильтров.
     * @returns Строка, представляющая часть URL с фильтрами.
     */
    build: function (catalogType: ICatalogQueryParams): string {
        let url = '/catalog';

        if (catalogType.query_params.length > 0) {
            url += `/${catalogType.query_params.join('/')}`;
        }
        if (catalogType.isBrand && catalogType.brand_name !== '') {
            url += `/brand/${catalogType.brand_name}`;
        }
        if (catalogType.filter_params.length > 0) {
            url += `/filter/`;

            url += catalogType.filter_params
                .map((filter) => {
                    if (filter.isRange) {
                        return `${filter.property}-from-${filter.values.join('-to-')}`;
                    } else {
                        return `${filter.property}-is-${filter.values.join('-and-')}`;
                    }
                })
                .join('/');

            url += '/apply';
        }

        return url;
    },

    /**
     * Добавляет категорию с указанным значением в параметры запроса каталога.
     * @param catalogType - Параметры запроса каталога для изменения.
     * @param responseVariable - Данные фильтра (чекбокса) для добавления.
     * @returns Обновленные параметры запроса каталога.
     */
    add: function (
        catalogType: ICatalogQueryParams,
        responseVariable: ICatalogFilterCheckbox,
    ): ICatalogQueryParams {
        const newFilter: TCatalogFilterType = {
            property: responseVariable.property,
            values: [],
            isRange: false,
        };

        if (responseVariable.isRange) {
            newFilter.values = [];
            newFilter.isRange = true;
        } else {
            newFilter.values = [responseVariable.value];
        }
        catalogType.filter_params = [...catalogType.filter_params, newFilter];

        return catalogType;
    },

    /**
     * Удаляет значение из категории в параметрах запроса каталога.
     * @param catalogType - Параметры запроса каталога для изменения.
     * @param responseVariable - Данные фильтра (чекбокса) для удаления.
     * @returns Обновленные параметры запроса каталога.
     */
    remove: function (
        catalogType: ICatalogQueryParams,
        responseVariable: ICatalogFilterCheckbox,
    ): ICatalogQueryParams {
        catalogType.filter_params = catalogType.filter_params.filter(
            (f) => f.property !== responseVariable.property,
        );

        return catalogType;
    },

    replace: function () {
        // только для Range
    },

    /**
     * Очищает все фильтры из параметров запроса каталога.
     * @param catalogType - Параметры запроса каталога для очистки.
     * @returns Очищенные параметры запроса каталога.
     */
    clear: function (catalogType: ICatalogQueryParams): ICatalogQueryParams {
        // очистка всех фильтров
        catalogType.filter_params = [];

        return catalogType;
    },

    /**
     * Добавляет дополнительный параметр в параметры запроса каталога.
     * @param catalogType - Параметры запроса каталога для изменения.
     * @param responseVariable - Данные фильтра (чекбокса) для добавления.
     * @param parameter - Параметр типа фильтра для добавления.
     * @returns Обновленные параметры запроса каталога.
     */
    include: function (
        catalogType: ICatalogQueryParams,
        responseVariable: ICatalogFilterCheckbox,
        parameter: TCatalogFilterType,
    ): ICatalogQueryParams {
        parameter.values.push(responseVariable.value);
        catalogType.filter_params = [
            ...catalogType.filter_params.filter((f) => f.property !== responseVariable.property),
            parameter,
        ];

        return catalogType;
    },

    /**
     * Удаляет параметр из параметров запроса каталога.
     * @param catalogType - Параметры запроса каталога для изменения.
     * @param responseVariable - Данные фильтра (чекбокса) для удаления.
     * @param parameter - Параметр типа фильтра для удаления.
     * @returns Обновленные параметры запроса каталога.
     */
    exclude: function (
        catalogType: ICatalogQueryParams,
        responseVariable: ICatalogFilterCheckbox,
        parameter: TCatalogFilterType,
    ): ICatalogQueryParams {
        if (catalogType.filter_params.length === 1) {
            if (catalogType.filter_params[0].values.length === 1) {
                // один целый фильтр с одним значением
                catalogType = this.clear(catalogType);
            } else {
                // один целый фильтр с несколькими значениями
                parameter.values = parameter.values.filter((f) => f !== responseVariable.value);
                catalogType.filter_params[0] = parameter;
            }
        } else {
            if (parameter.values.length === 0) {
                catalogType = this.remove(catalogType, responseVariable);
            } else {
                catalogType.filter_params = [
                    ...catalogType.filter_params.filter(
                        (f) => f.property !== responseVariable.property,
                    ),
                    parameter,
                ];
            }
        }

        return catalogType;
    },
};
